import { API_URL } from "config/environments"
import { isUndefined, random } from "lodash"

import { encryptionHelper } from "helpers/encryption"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { ZipWriter, BlobWriter, BlobReader } from "@zip.js/zip.js"

export enum ACTION_DOWNLOAD_WORKER {
  START = "DOWNLOAD_WORKER_START",
  PROCESS = "DOWNLOAD_WORKER_PROCESS",
  FINISH = "DOWNLOAD_WORKER_FINISH",
}

interface DataRequestDownloadOneFile {
  assetPath: string
  idConversationParam?: string
  project_build_id?: string
  projectId?: string
  project_component_history_id?: string
  decryptedKey?: string
  vaultKey?: string
  dbKey?: string
}
interface DownloadQueueItem {
  apiURL: string
  name: string
  subName: string
  downloadId: string
  buildId?: string
  done?: boolean
  errorMessage?: string
  isFileOne?: boolean
  dataFileOne?: DataRequestDownloadOneFile
  dbKey?: string
}

/* eslint-disable no-restricted-globals */
self.onmessage = async (event: MessageEvent) => {
  if (event.data.action !== ACTION_DOWNLOAD_WORKER.START) return
  const { itemQueue, tokenAuth, multipleFiles, fileFolderName } = event.data
    .payload as {
      itemQueue: DownloadQueueItem
      tokenAuth: string
      multipleFiles: any
      fileFolderName?: string
    }

  if (itemQueue.isFileOne && !isUndefined(itemQueue.dataFileOne)) {
    await handleDownloadOneFile(
      itemQueue.dataFileOne,
      itemQueue.downloadId,
      tokenAuth
    )
  } else {
    await handleDownloadMultipleFile(
      itemQueue.buildId || "",
      multipleFiles,
      itemQueue.downloadId,
      tokenAuth,
      itemQueue.apiURL,
      fileFolderName || "",
      itemQueue.dbKey
    )
  }
}

const decryptFileByGCM = async (
  fileResponse: Response,
  downloadId: string,
  key: string,
  hash_key?: string,
  public_key?: string,
  isPostMessage: boolean = true
) => {
  console.log("------- DECRYPT FILE BY GCM-----------")
  if (!fileResponse.body) {
    return {
      dataBlob: null,
      messageError: "File not found or empty",
    }
  }

  const decryptKey = await encryptionGCMHelper.decryptKeyByCrypto(key)
  const decryptedStream = encryptionGCMHelper.decryptGCMStream(
    fileResponse.body,
    decryptKey
  )
  const dataBlob = await new Response(decryptedStream).blob()
  if (dataBlob.size === 0) {
    return {
      dataBlob: null,
      messageError: "File is empty",
    }
  }
  if (isPostMessage) {
    self.postMessage({
      action: ACTION_DOWNLOAD_WORKER.PROCESS,
      payload: {
        message: "Verify signature...",
        downloadId,
        process: random(70, 80),
      },
    })
  }

  if (!hash_key || !public_key) {
    return {
      dataBlob,
    }
  }

  const publicCryptoKey = await encryptionGCMHelper.coverPublicKeyToCryptoKey(
    public_key
  )
  let verified = true
  if (hash_key.length === 64) {
    // verify file to server email
    verified = await encryptionGCMHelper.verifyBlobHash(dataBlob, hash_key)
  } else {
    const hashBuffer = await encryptionGCMHelper.calculateSHA256(dataBlob)
    const signatureBuffer = encryptionGCMHelper.base64ToArrayBuffer(hash_key)
    if (!publicCryptoKey) {
      return {
        dataBlob: null,
        messageError: "Invalid public key",
      }
    }
    verified = await crypto.subtle.verify(
      { name: "RSA-PSS", saltLength: 32 },
      publicCryptoKey,
      signatureBuffer,
      hashBuffer
    )
  }

  console.log("Hash_key:", hash_key)
  console.log("Signature", verified ? "//SUCCESS//" : "//FAILED//")
  if (verified) {
    return {
      dataBlob,
    }
  }
  return {
    dataBlob: null,
    messageError: "Signature verification failed",
  }
}

const decryptFileByCBC = async (
  fileResponse: Response,
  assetPath: string,
  key: string,
  downloadId: string,
  hash_key?: string,
  public_key?: string,
  isPostMessage: boolean = true
) => {
  console.log("------- DECRYPT FILE BY CBC-----------")

  const isSpecificationFileHtml =
    assetPath.includes("pcb/gerber/specification") &&
    assetPath.endsWith(".html")
  let blob: any = await fileResponse.blob()
  let temp = await blob.text()
  blob = null // Free memory
  const data = await encryptionGCMHelper.decryptContext(key, temp, true)
  temp = null // Free memory
  let newBlob: any = new Blob([data])
  if (newBlob.size === 0) {
    return {
      dataBlob: null,
      messageError: "File is empty",
    }
  }
  if (isPostMessage) {
    self.postMessage({
      action: ACTION_DOWNLOAD_WORKER.PROCESS,
      payload: {
        message: "Verify signature...",
        downloadId,
        process: random(70, 80),
      },
    })
  }

  let fileText: any = await newBlob.text()
  if (hash_key && public_key) {
    const verified = encryptionHelper.checkVerifiedSignature(
      public_key,
      hash_key,
      fileText
    )
    fileText = null // Free memory
    console.log("Hash_key:", hash_key)
    console.log("Signature", verified ? "//SUCCESS//" : "//FAILED//")
    if (verified) {
      return {
        dataBlob: newBlob,
      }
    } else {
      if (!isSpecificationFileHtml) {
        return {
          dataBlob: null,
          messageError: "Signature verification failed",
        }
      }
    }
  }

  return {
    dataBlob: newBlob,
  }
}

const fetchFileAndSignature = async (
  assetPath: string,
  key: string,
  tokenAuth: string,
  downloadId: string,
  extraParams?: Record<string, any>,
  isPostMessage: boolean = true,
  dbKey?: string
) => {
  try {
    console.log("-------START DECRYPT FILE-----------")
    console.log("Decrypt key:", key)
    console.log("Asset path:", assetPath)
    const fileURL = encryptionGCMHelper.getFullAssetURI(assetPath, extraParams)

    const [fileResponse, hashKeyResponse] = await Promise.all([
      fetch(fileURL, {
        headers: {
          Authorization: tokenAuth,
          ...(dbKey ? { "x-db-key": dbKey } : {}),
        },
      }),
      fetch(
        `${API_URL}/api/file/hash?file_key=${encodeURIComponent(assetPath)}`,
        {
          headers: {
            Authorization: tokenAuth,
            "Content-Type": "application/json",
            ...(dbKey ? { "x-db-key": dbKey } : {}),
          },
        }
      ),
    ])

    const metadataResponse = fileResponse.headers.get("x-meta-data")
    const { hash_key, public_key } = await hashKeyResponse.json()
    const metadataJson: {
      "mime-type": string
      "encrypt-version": string
    } = metadataResponse ? JSON.parse(metadataResponse) : {}
    console.log("metadataJson", metadataJson)
    if (isPostMessage) {
      self.postMessage({
        action: ACTION_DOWNLOAD_WORKER.PROCESS,
        payload: {
          message: "Decrypting file...",
          downloadId,
          process: random(15, 30),
        },
      })
    }
    if (metadataJson["encrypt-version"]) {
      return await decryptFileByGCM(
        fileResponse,
        downloadId,
        key,
        hash_key,
        public_key,
        isPostMessage
      )
    }
    try {
      const gcmResult = await decryptFileByGCM(
        fileResponse.clone(),
        downloadId,
        key,
        hash_key,
        public_key,
        false
      )

      if (!gcmResult.messageError) {
        return gcmResult
      }
    } catch (e) {
    }

    return await decryptFileByCBC(
      fileResponse,
      assetPath,
      key,
      downloadId,
      hash_key,
      public_key,
      isPostMessage
    )
  } catch (err) {
    console.error("Verification error:", err)
    return {
      dataBlob: null,
      messageError: "Error verifying file signature",
    }
  } finally {
    console.log("-------END DECRYPT FILE-----------")
  }
}

const handleDownloadOneFile = async (
  dataRequest: DataRequestDownloadOneFile,
  downloadId: string,
  tokenAuth: string
) => {
  const {
    assetPath,
    idConversationParam,
    project_build_id,
    project_component_history_id,
  } = dataRequest

  const params: Record<string, string> = {}
  if (project_build_id) params["project_build_id"] = project_build_id
  if (project_component_history_id && !idConversationParam)
    params["project_component_history_id"] = project_component_history_id
  if (idConversationParam) params["conversation_id"] = idConversationParam

  const { dataBlob, messageError } = await fetchFileAndSignature(
    assetPath,
    dataRequest.decryptedKey || "",
    tokenAuth,
    downloadId,
    params,
    undefined,
    dataRequest.dbKey
  )

  self.postMessage({
    action: ACTION_DOWNLOAD_WORKER.FINISH,
    payload: {
      dataBlob,
      messageError,
      downloadId,
    },
  })
}

const handleDownloadMultipleFile = async (
  buildId: string,
  multipleFiles: any[],
  downloadId: string,
  tokenAuth: string,
  apiURL: string,
  fileFolderName: string,
  dbKey?: string
) => {
  console.log("-------START DOWNLOAD MULTIPLE FILES-----------")
  const conversationId = getParameterByName("conversation_id", apiURL)
  const zipWriter = new ZipWriter(new BlobWriter("application/zip"))
  let zipMessageError: string = ""
  const existingPaths = new Set<string>()
  const process = 75 / multipleFiles.length
  let currentProgress = 15

  for (let i = 0; i < multipleFiles.length; i++) {
    const file = multipleFiles[i]
    const newProcess = (currentProgress += process)
    self.postMessage({
      action: ACTION_DOWNLOAD_WORKER.PROCESS,
      payload: {
        message: `Decrypting and verifying signature:${i + 1}/${multipleFiles.length
          }`,
        downloadId,
        process: newProcess,
      },
    })
    if (file.buffer) {
      let fileContent = file.buffer
      if (file.messages && file.messages.length > 0) {
        for (const message of file.messages) {
          const rawMessage = (await encryptionGCMHelper.decryptContext(
            message.messageKey,
            message.message
          )) as string
          fileContent = fileContent.replace(message.uniqueKey, rawMessage)
        }
      }
      if (
        (file.file.split("/").slice(-1)[0] as string).search("txt") !== -1 ||
        (file.file.split("/").slice(-1)[0] as string).search("csv") !== -1
      ) {
        const blob = new Blob([fileContent])
        const fileNameBuffer = file.file.split("/").slice(-1)[0]
        await zipWriter.add(
          `${file.keyToFolder}/${removeTimestampInFileName(fileNameBuffer)}`,
          new BlobReader(blob)
        )
      }
    } else if (file.file && !file.buffer && file.buffer !== "") {
      try {
        const isSpecificationFileHtml =
          file.file.includes("pcb/gerber/specification") &&
          file.file.endsWith(".html")
        const params: Record<string, string> = {}
        const isAdditionalInfo =
          file.keyToFolder === "additional_info" && buildId
        if (isAdditionalInfo) params["project_build_id"] = buildId
        if (conversationId) params["conversation_id"] = conversationId
        const { dataBlob, messageError } = await fetchFileAndSignature(
          file.file,
          file.key,
          tokenAuth,
          downloadId,
          params,
          false, // Disable post message for this fetch
          dbKey
        )
        if (messageError && isSpecificationFileHtml) {
          continue
        } else if (messageError) {
          // zipMessageError = messageError
          // break
          continue
        }
        const fileName = file.file.split("/").slice(-1)[0]

        const uniqueName = getUniqueFileName(
          existingPaths,
          file.keyToFolder,
          removeTimestampInFileName(fileName)
        )
        await zipWriter.add(uniqueName, new BlobReader(dataBlob))
      } catch (error) {
        console.error("Error fetching file:", error)
        zipMessageError =
          "Decryption or verification failed for one or more files."
        break
      }
    }
  }
  if (zipMessageError) {
    self.postMessage({
      action: ACTION_DOWNLOAD_WORKER.FINISH,
      payload: {
        dataBlob: null,
        messageError: zipMessageError,
        downloadId,
      },
    })
    return
  }
  self.postMessage({
    action: ACTION_DOWNLOAD_WORKER.PROCESS,
    payload: {
      message: "Zipping file...",
      downloadId,
      process: 97, // 97% after processing files
    },
  })
  await delay(2000) // Delay to ensure the last message is processed
  const blobZip = await zipWriter.close()

  self.postMessage({
    action: ACTION_DOWNLOAD_WORKER.FINISH,
    payload: {
      dataBlob: blobZip,
      messageError: "",
      downloadId,
      fileFolderName,
    },
  })
  console.log("-------END DOWNLOAD MULTIPLE FILES-----------")
}

const getParameterByName = (name: string, url: string) => {
  name = name.replace(/[\[\]]/g, "\\$&")
  var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
    results = regex.exec(url)
  if (!results) return undefined
  if (!results[2]) return undefined
  return decodeURIComponent(results[2].replace(/\+/g, " "))
}

const removeTimestampInFileName = (fileName: string) => {
  return fileName.replace(/_(\d+)(?=\.[^.]+$)/, "")
}

export function getUniqueFileName(
  existingPaths: Set<string>,
  folderPath: string | null,
  fileName: string
): string {
  const baseName = fileName.replace(/(\.[^.]*)$/, "")
  const extensionMatch = fileName.match(/(\.[^.]*)$/)
  const extension = extensionMatch ? extensionMatch[1] : ""

  const folderPrefix = folderPath ? `${folderPath}/` : ""
  let newFilePath = `${folderPrefix}${fileName}`
  let counter = 1

  while (existingPaths.has(newFilePath)) {
    newFilePath = `${folderPrefix}${baseName} (${counter})${extension}`
    counter++
  }

  existingPaths.add(newFilePath)
  return newFilePath
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
